import {
  format,
  isSameYear,
  isThisYear,
  isToday,
  isYesterday,
  fromUnixTime,
  formatDistanceToNow,
  differenceInCalendarDays,
  differenceInDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PT_BR_LOCALE = ptBR;

const toDate = value => {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return fromUnixTime(value);
  return new Date(value);
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'HH:mm') => {
  const dateValue = toDate(time);
  const normalizedFormat = dateFormat
    .replace(/hh:mm\s*a|h:mm\s*a/g, 'HH:mm')
    .replace(/LLL\s*d,\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d/g, 'dd/MM');

  return format(dateValue, normalizedFormat, { locale: PT_BR_LOCALE });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'dd/MM/yyyy') => {
  const messageTime = toDate(time);
  const now = new Date();
  const normalizedFormat = dateFormat
    .replace(/hh:mm\s*a|h:mm\s*a/g, 'HH:mm')
    .replace(/MMM\s*d,\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d,\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d/g, 'dd/MM');
  const messageDate = format(messageTime, normalizedFormat, {
    locale: PT_BR_LOCALE,
  });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'dd/MM/yyyy, HH:mm', {
      locale: PT_BR_LOCALE,
    });
  }
  return messageDate;
};

/**
 * Data de uma mensagem na conversa, sempre com a hora.
 *
 * Em vez de repetir "20/09" em toda mensagem, os dias recentes aparecem pelo nome, que é
 * como as pessoas leem a conversa: hoje, ontem e o dia da semana na última semana.
 * A partir daí volta a data, porque o nome do dia deixa de ser suficiente.
 *
 * @param {number|string|Date} time
 * @param {Date} [now] - referência, para teste
 * @returns {string} "Hoje, 16:45", "Ontem, 16:45", "sáb, 16:45" ou "20/09/2025, 16:45"
 */
export const messageDayTimestamp = (time, now = new Date()) => {
  const date = toDate(time);
  if (Number.isNaN(date?.getTime?.())) return '';

  const hour = format(date, 'HH:mm', { locale: PT_BR_LOCALE });
  const days = differenceInCalendarDays(now, date);

  if (days === 0) return `Hoje, ${hour}`;
  if (days === 1) return `Ontem, ${hour}`;
  // Dias futuros (relógio adiantado no cliente) caem no formato com data.
  if (days > 1 && days < 7) {
    return `${format(date, 'EEEEEE', { locale: PT_BR_LOCALE })}, ${hour}`;
  }
  if (isSameYear(date, now)) {
    return `${format(date, 'dd/MM', { locale: PT_BR_LOCALE })}, ${hour}`;
  }
  return `${format(date, 'dd/MM/yyyy', { locale: PT_BR_LOCALE })}, ${hour}`;
};

/**
 * Formats a timestamp relative to the current day for compact activity lists.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  if (isToday(date)) return format(date, 'HH:mm', { locale: PT_BR_LOCALE });
  if (isYesterday(date)) return yesterdayLabel;
  if (isThisYear(date)) return format(date, 'dd/MM', { locale: PT_BR_LOCALE });
  return format(date, 'dd/MM/yyyy', { locale: PT_BR_LOCALE });
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const dateValue = toDate(time);
  return formatDistanceToNow(dateValue, {
    addSuffix: true,
    locale: PT_BR_LOCALE,
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'dd/MM/yyyy') => {
  const dateValue = toDate(time);
  const normalizedFormat = df
    .replace(/MMM\s*d,\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d,\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d\s*yyyy/g, 'dd/MM/yyyy')
    .replace(/LLL\s*d/g, 'dd/MM')
    .replace(/hh:mm\s*a|h:mm\s*a/g, 'HH:mm');

  return format(dateValue, normalizedFormat, { locale: PT_BR_LOCALE });
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const normalized = String(time || '')
    .toLowerCase()
    .trim();

  if (
    /less than a minute|in less than a minute|menos de um minuto|menos de 1 minuto/.test(
      normalized
    )
  ) {
    return 'now';
  }

  const resolveNumber = unitRegex => {
    const directNumber = normalized.match(/\d+/);
    if (directNumber) return Number(directNumber[0]);

    if (unitRegex.minute && /(a minute|an minute|um minuto)/.test(normalized)) {
      return 1;
    }
    if (unitRegex.hour && /(an hour|a hour|uma hora)/.test(normalized)) {
      return 1;
    }
    if (unitRegex.day && /(a day|um dia)/.test(normalized)) {
      return 1;
    }
    if (unitRegex.month && /(a month|um m[eê]s)/.test(normalized)) {
      return 1;
    }
    if (unitRegex.year && /(a year|um ano)/.test(normalized)) {
      return 1;
    }
    return null;
  };

  const unitRegex = {
    minute: /(minute|minuto)/,
    hour: /(hour|hora)/,
    day: /(day|dia)/,
    month: /(month|m[eê]s)/,
    year: /(year|ano)/,
  };

  const number = resolveNumber(unitRegex);
  if (number !== null) {
    if (unitRegex.minute.test(normalized)) return `${number}m${suffix}`;
    if (unitRegex.hour.test(normalized)) return `${number}h${suffix}`;
    if (unitRegex.day.test(normalized)) return `${number}d${suffix}`;
    if (unitRegex.month.test(normalized)) return `${number}mo${suffix}`;
    if (unitRegex.year.test(normalized)) return `${number}y${suffix}`;
  }

  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
